import { ClerkProvider, useAuth } from '@clerk/react-router';
import { TooltipProvider } from '@plato/ui/primitives/tooltip';
import { BreakpointProvider } from '@plato/ui/util/use-breakpoint';
import { Fragment, type ReactElement, type ReactNode } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { CLERK_PUBLISHABLE_KEY } from '../../lib/constants.ts';
import { useImpersonation } from '../../lib/hooks/use-impersonation.ts';
import { i18n } from '../../lib/i18n/i18n.ts';
import { languageConfigs, toSupportedLanguage } from '../../lib/i18n/language-config.ts';
import { Toaster } from '../ui/toaster.tsx';
import { TRPCProvider } from './trpc-provider.tsx';

if (!CLERK_PUBLISHABLE_KEY) {
    throw new Error('Missing Clerk Publishable Key');
}

export type AppProvidersProps = {
    children?: ReactNode;
};

export function AppProviders({ children }: AppProvidersProps): ReactElement {
    const {
        i18n: { language: currentLanguage },
    } = useTranslation();
    const { clerkLocalization } = languageConfigs[toSupportedLanguage(currentLanguage)];
    return (
        <HelmetProvider>
            <ClerkProvider
                publishableKey={CLERK_PUBLISHABLE_KEY}
                appearance={{
                    variables: {
                        colorPrimary: '#103FEF',
                    },
                    elements: {
                        footer: 'hidden',
                    },
                }}
                localization={clerkLocalization}
                signInUrl="/sign-in"
            >
                <I18nextProvider i18n={i18n}>
                    <UserProvider>
                        <TRPCProvider>
                            <BreakpointProvider>
                                <TooltipProvider>
                                    {children}
                                    <Toaster />
                                </TooltipProvider>
                            </BreakpointProvider>
                        </TRPCProvider>
                    </UserProvider>
                </I18nextProvider>
            </ClerkProvider>
        </HelmetProvider>
    );
}

function UserProvider({ children }: AppProvidersProps): ReactElement {
    const { userId } = useAuth();
    const [impersonatedUserId] = useImpersonation();
    // force children to remount if current logged in identity changes
    return <Fragment key={impersonatedUserId ?? userId}>{children}</Fragment>;
}
